import { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Spinner,
    UncontrolledTooltip,
    Badge
} from 'reactstrap';
import { FaSearch, FaEdit, FaTrash, FaUser, FaPlus, FaRegBuilding, FaUsers, FaEye, FaBuilding, FaCheck } from 'react-icons/fa';
import { CLIENT_FORM_PAGE } from '../../routes/routeConstants';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clientActions } from '../../sagas/clientSlice';
import { formatPrice } from '../../helpers/utility';
import Swal from 'sweetalert2';

const ClientList = () => {

    const navigate = useNavigate();
    const location = useLocation();

    const getClientFormPath = () => {
        if (location.pathname.startsWith('/sales-module')) {
            return '/sales-module/clients/new';
        }
        return CLIENT_FORM_PAGE;
    };

    const getClientEditPath = (client) => {
        if (location.pathname.startsWith('/sales-module')) {
            return `/sales-module/clients/${client.id}/edit`;
        }
        return CLIENT_FORM_PAGE;
    };

    // États
    const { clients, loading } = useSelector(state => state.clients);
    const [filteredClients, setClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const { t } = useTranslation();
    const dispatch = useDispatch();


    useEffect(() => {
        dispatch(clientActions.findAll({
            query: ""
        }));// Appel de l'action pour récupérer les clients    
    }, []);

    // Filtrage et recherche
    useEffect(() => {
        if (loading) return; // Ne pas exécuter si le chargement est en cours
        let result = clients;


        // Recherche
        if (searchTerm) {
            result = result.filter(client => {
                const clientName = client.typeClient === 'PARTICULIER' ? `${client.nom} ${client.prenom}` : client.entreprise;
                return clientName.toLowerCase().includes(searchTerm.toLowerCase()) || client.code.toString().includes(searchTerm);
            })
        }

        setClients(result);
        setCurrentPage(1);
    }, [searchTerm, clients]);


    const handleDeleteClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera désactivé et ne pourra plus être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, désactiver !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Inactivé !',
                            'Le client a été désactivé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.findAll({
                            query: ""
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la désactivation.',
                            'error'
                        );
                    }
                }

                dispatch(clientActions.inactivate(payload));
            }
        });
    };

    const handleActiverClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera activé et pourra être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, activer !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Activé !',
                            'Le client a été activé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.findAll({
                            query: ""
                        })); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de l\'activation.',
                            'error'
                        );
                    }
                }
                dispatch(clientActions.activate(payload));
            }
        });
    };

    const handleEdit = (client) => {
        const path = getClientEditPath(client);
        navigate(path, { state: { client } });
    };

    return (
        <div>
            {/* Header Section */}

            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUsers className="text-primary me-2" />
                                {t('client.list')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="primary" onClick={() => navigate(getClientFormPath())}>
                                    <FaPlus className="mr-2" /> {t('client.newClient')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? ( // Show spinner when loading
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="Rechercher par nom, email, téléphone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="form-control"
                                    />
                                </div>
                            </div>

                            <table className="table table-hover table-striped table-bordered" >
                                <thead className="thead-light">
                                    <tr>
                                        <th className="align-middle text-center">{t('client.code')}</th>
                                        <th className="align-middle text-center">{t('client.nom')}</th>
                                        <th className="align-middle text-center">{t('fournisseur.contact')}</th>
                                        <th className="align-middle text-center">{t('client.chantiers')}</th>
                                        <th className="align-middle text-center">{t('client.totalDebit')}</th>
                                        <th className="align-middle text-center">{t('client.totalCredit')}</th>
                                        <th className="align-middle text-center">{t('client.solde')}</th>
                                        <th className="align-middle text-center">{t('client.active')}</th>
                                        <th className="align-middle text-center">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredClients.map(client => (
                                        <tr key={client.id}>
                                            <td>
                                                <strong>{client.code}</strong>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    <div className="me-3">
                                                        {client.typeClient === 'PARTICULIER' ? (
                                                            <FaUser className="text-primary" />
                                                        ) : (
                                                            <FaRegBuilding className="text-info" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold">
                                                            {client.typeClient === 'PARTICULIER'
                                                                ? `${client.nom} ${client.prenom}`
                                                                : client.entreprise}
                                                        </div>
                                                        <small className="text-muted">
                                                            {client.typeClient === 'PARTICULIER' ? 'Particulier' : 'Entreprise'}
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                
                                                    <div>
                                                        <div className="fw-bold">
                                                            {client.nomContact || '-'}
                                                        </div>
                                                        <small className="text-muted">
                                                            {client.telephone1 || client.telephone2 || client.email || '-' }
                                                        </small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {client.chantiers && client.chantiers.length > 0 ? (
                                                    <div>
                                                        <div className="d-flex align-items-center mb-1">
                                                            <FaBuilding className="text-primary me-2" />
                                                            <span className="fw-bold">{client.chantiers.length} chantier{client.chantiers.length > 1 ? 's' : ''}</span>
                                                        </div>
                                                        <div className="small">
                                                            {client.chantiers.slice(0, 2).map((chantier, index) => (
                                                                <div key={chantier.id} className="text-muted">
                                                                    <span className="fw-semibold">{chantier.code}</span> - {chantier.nom}
                                                                </div>
                                                            ))}
                                                            {client.chantiers.length > 2 && (
                                                                <div className="text-muted small">
                                                                    +{client.chantiers.length - 2} autre{client.chantiers.length - 2 > 1 ? 's' : ''}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-muted text-center">
                                                        <small>Aucun chantier</small>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(client.totalDebit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="fw-bold">
                                                    {formatPrice(client.totalCredit || 0)}
                                                </span>
                                            </td>
                                            <td>
                                                {Number(client.solde) < 0 ? (
                                                    <span className="text-success fw-bold">+{formatPrice(Math.abs(client.solde))}</span>
                                                ) : Number(client.solde) > 0 ? (
                                                    <span className="text-danger fw-bold">-{formatPrice(Math.abs(client.solde))}</span>
                                                ) : (
                                                    <span className="text-dark fw-bold">{formatPrice(0)}</span>
                                                )}
                                            </td>
                                            <td>
                                                {client.active ? (
                                                    <Badge color="success">Actif</Badge>
                                                ) : (
                                                    <Badge color="danger">Bloqué</Badge>
                                                )}
                                            </td>
                                            <td>

                                                <Button
                                                    color="warning"
                                                    size="sm"
                                                    onClick={() => handleEdit(client)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                {client.active ? <Button
                                                    className="ms-1"
                                                    color="danger"
                                                    title='Désactiver'
                                                    size="sm"
                                                    onClick={() => handleDeleteClient(client.id)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                                    :
                                                <Button
                                                    className="ms-1"
                                                    color="success"
                                                    size="sm"
                                                    title='Activer'
                                                    onClick={() => handleActiverClient(client.id)}
                                                >
                                                    <FaCheck />
                                                </Button>
                                                }
                                            </td>
                                        </tr>
                                    ))}

                                    {filteredClients.length === 0 && (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">
                                                <div className="text-muted">{t('delivery.noBL')}</div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default ClientList;